/**
 * React side of the Phaser bounds protocol (see ../phaserBoundsReporter.ts).
 *
 * While target ids are requested, the hook re-asks on a short cadence —
 * scripted plays, travel animations, the deal reveal and previews all move
 * sprites without DOM signals, so gentle polling (tutorial-only) is the
 * robust way to stay glued to the real objects. Also re-requests on window
 * and visualViewport resizes.
 */
import { useEffect, useMemo, useState } from 'react';
import type { SpotRect } from './spotlightGeometry';
import {
  BOUNDS_EVENT,
  REQUEST_BOUNDS_EVENT,
  type BoundsDetail,
  type BoundsRequestDetail,
} from '../phaserBoundsReporter';

const POLL_MS = 300;

export function usePhaserTargets(targetIds: string[]): Record<string, SpotRect[]> {
  const [bounds, setBounds] = useState<Record<string, SpotRect[]>>({});
  // Stable dependency for the effect regardless of array identity.
  const key = targetIds.join('|');
  const ids = useMemo(() => key.length ? key.split('|') : [], [key]);

  useEffect(() => {
    if (ids.length === 0) {
      setBounds({});
      return;
    }

    const onBounds = (e: Event) => {
      const detail = (e as CustomEvent<BoundsDetail>).detail;
      if (detail?.bounds) setBounds(detail.bounds);
    };
    const request = () => {
      window.dispatchEvent(new CustomEvent<BoundsRequestDetail>(REQUEST_BOUNDS_EVENT, {
        detail: { targets: ids },
      }));
    };

    window.addEventListener(BOUNDS_EVENT, onBounds);
    window.addEventListener('resize', request);
    window.visualViewport?.addEventListener('resize', request);
    request();
    const interval = window.setInterval(request, POLL_MS);
    return () => {
      window.removeEventListener(BOUNDS_EVENT, onBounds);
      window.removeEventListener('resize', request);
      window.visualViewport?.removeEventListener('resize', request);
      window.clearInterval(interval);
      setBounds({});
    };
  }, [ids]);

  return bounds;
}
